import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { DEFAULT_CURRENCY } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsFormatters';

import { useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useGetVendor } from '~/hooks/apis/vendors/useGetVendor';

import { getFxRates } from '~/services/fxService';

import {
  type LocationField,
  applyLocationChange,
  deriveCurrencyOptions,
  derivePropertySetup,
  netRateFor,
  resolveFxRate,
} from './propertySetupModel';
import type { PropertySetupValues } from './types';

export function usePropertySetup(offerId: string | undefined) {
  const [values, setValues] = useState<PropertySetupValues | null>(null);

  const { data: offer, isFetched: isOfferFetched } = useGetOffer(offerId);
  const vendorId = offer?.vendor_account_id;
  const { data: account, isFetched: isAccountFetched } = useGetVendor(vendorId);
  const { data: opportunity, isFetched: isOpportunityFetched } = useGetOpportunity<App.OpportunityPage>(offerId, {
    enabled: !!offerId,
  });
  // isFetched is also true after an error, so a failed lookup still prefills (blank fields).
  const { data: related, isFetched: isRelatedFetched } = useGetOpportunityRelated(offerId);
  const isPrefillReady = isOfferFetched && isOpportunityFetched && isRelatedFetched && (!vendorId || isAccountFetched);
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: getFxRates, staleTime: Infinity });

  // Ref guard: a background refetch must not clobber user edits.
  const prefilledFor = useRef<string | undefined>(undefined);
  // Without this, a cleared fxRate re-populates on the next render (AUD always resolves to 1).
  const fxAutoPopulated = useRef(false);

  useEffect(() => {
    setValues(null);
    prefilledFor.current = undefined;
    fxAutoPopulated.current = false;
  }, [offerId]);

  useEffect(() => {
    if (!isPrefillReady || prefilledFor.current === offerId) {
      return;
    }

    const roomAssets = [...(related?.deals ?? []), ...(related?.rooms ?? [])]
      .map((deal) => deal.room_asset)
      .filter((asset): asset is OpportunityRoomAsset => Boolean(asset));

    setValues(derivePropertySetup({ roomAssets, accountName: account?.name, opportunity, deals: related?.deals }));
    prefilledFor.current = offerId;
  }, [offerId, isPrefillReady, account, opportunity, related]);

  useEffect(() => {
    if (fxAutoPopulated.current || values?.fxRate !== null) {
      return;
    }

    if (!values.currency) {
      return;
    }

    const rate = resolveFxRate(fxRates, values.currency);
    if (rate === null) {
      return;
    }

    setValues({ ...values, fxRate: rate });
    fxAutoPopulated.current = true;
  }, [values, fxRates]);

  const setField = useCallback(<K extends keyof PropertySetupValues>(field: K, value: PropertySetupValues[K]) => {
    setValues((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  // Marks prefill done so the prefill effect doesn't overwrite loaded draft values.
  const loadValues = useCallback(
    (loaded: PropertySetupValues) => {
      prefilledFor.current = offerId;
      fxAutoPopulated.current = true;
      setValues(loaded);
    },
    [offerId],
  );

  const autoResolveFxRate = useCallback(() => {
    setValues((current) => {
      if (!current?.currency) {
        return current;
      }

      return { ...current, fxRate: resolveFxRate(fxRates, current.currency) };
    });
  }, [fxRates]);

  const setCurrency = useCallback(
    (currency: string | null) => {
      setValues((current) => (current ? { ...current, currency, fxRate: resolveFxRate(fxRates, currency) } : current));
    },
    [fxRates],
  );

  const setLocation = useCallback((field: LocationField, value: string | null) => {
    setValues((current) => (current ? applyLocationChange(current, field, value) : current));
  }, []);

  // One update, so a currency and its FX land together instead of setCurrency resetting the rate in between.
  const applyImport = useCallback((changes: Partial<PropertySetupValues>) => {
    setValues((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const isLoaded = values !== null;
  const currency = values?.currency ?? DEFAULT_CURRENCY;
  const fxRate = values?.fxRate ?? null;
  const fx = useMemo(() => {
    const rate = isLoaded ? netRateFor({ currency, fxRate }) : undefined;
    const audToSourceCurrencyRate: Record<string, number | undefined> = { [currency]: rate, [DEFAULT_CURRENCY]: 1 };
    return { currency, rate, audToSourceCurrencyRate, isLoading: !isLoaded };
  }, [currency, fxRate, isLoaded]);

  const currencyOptions = useMemo(() => deriveCurrencyOptions(fxRates), [fxRates]);

  // Own currency uses the editable rate, so a period in it converts at the rate the PCM set.
  const audRateFor = useCallback(
    (rateCurrency: string) => (rateCurrency === currency ? (fx.rate ?? null) : resolveFxRate(fxRates, rateCurrency)),
    [currency, fx.rate, fxRates],
  );

  const todayRateFor = useCallback((rateCurrency: string) => resolveFxRate(fxRates, rateCurrency), [fxRates]);

  return {
    values,
    fx,
    setField,
    setCurrency,
    setLocation,
    applyImport,
    todayRateFor,
    currencyOptions,
    audRateFor,
    autoResolveFxRate,
    loadValues,
  };
}
